import {
  type ApiParsedResponseContext,
  type ApiResponse,
  createApiClient,
  extractApiFailureBody,
} from '@/services/api';
import { useAuth } from '@clerk/clerk-react';
import { useCallback, useMemo, useRef } from 'react';

/**
 * FEAT-APR08-001: payload of the global "subscription-required" event the
 * useApi 402 interceptor dispatches. The SubscribeBanner / upgrade modal
 * listens for this event and opens an upsell dialog without losing the
 * user's form data.
 */
export interface SubscriptionRequiredEventDetail {
  code: string;
  subscription_status: string;
  subscription_source: 'self' | 'gym';
  checkout_url: string;
  endpoint: string;
}

type ApiMethods = Pick<
  ReturnType<typeof createApiClient>,
  'get' | 'post' | 'put' | 'patch' | 'delete'
>;

interface UseApiReturn {
  api: ApiMethods;
  getToken: () => Promise<string | null>;
}

function dispatchSubscriptionRequired({
  endpoint,
  response,
  body,
}: ApiParsedResponseContext): void {
  if (response.status !== 402 || typeof window === 'undefined') return;

  const data = extractApiFailureBody(body);
  const detail: SubscriptionRequiredEventDetail = {
    code: data.code || 'subscription_required',
    subscription_status: data.subscription_status || 'pending',
    subscription_source: data.subscription_source || 'self',
    checkout_url: data.checkout_url || '/api/auth/create-checkout-session',
    endpoint,
  };
  window.dispatchEvent(
    new CustomEvent<SubscriptionRequiredEventDetail>('subscription-required', { detail })
  );
}

/**
 * Provides the shared API client with Clerk's synchronized token getter and
 * the hook-only subscription-required event adapter.
 */
export function useApi(): UseApiReturn {
  const { getToken } = useAuth();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  const stableGetToken = useCallback(() => getTokenRef.current(), []);
  const api = useMemo<ApiMethods>(() => {
    const client = createApiClient(stableGetToken, {
      failureMetadata: 'subscription',
      onParsedResponse: dispatchSubscriptionRequired,
    });

    return {
      get: client.get.bind(client),
      post: client.post.bind(client),
      put: client.put.bind(client),
      patch: client.patch.bind(client),
      delete: client.delete.bind(client),
    };
  }, [stableGetToken]);

  return { api, getToken };
}

export type { ApiResponse };
